<script lang="ts">
  import { tick, type Snippet } from "svelte"
  import { mdiCalendar, mdiCalendarMonthOutline, mdiTable } from "@mdi/js"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import {
    type ColumnDef,
    type DataRow,
    type TableGetRows,
    type TableRowsUpdater,
  } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
  import { InboxWorkflowDefaultBulkActions } from "@kontextwerk/control-web-frame"
  import AppointmentPlanningCalendarPreview from "./_modules/appointment-planning-calendar-preview"
  import type { AppointmentPlanningStartBound } from "../../_shared/_logic/start-bound"
  import AppointmentPlanningPreviewTable from "./_modules/appointment-planning-preview-table"
  import AppointmentPlanningFilterBar from "./_modules/appointment-planning-filter-bar"
  import type {
    LoadingPreviewRow,
    PlanningPreviewRow,
    PlanningPreviewRowChange,
    PlanningPreviewSelectOption,
    PlanningTableRowControlCommand,
    PlanningTableRowControlsResolver,
    PlanningTableRowStateResolver,
    PreviewTableRow,
  } from "../../_shared/_logic/table-contract"
  import {
    APPOINTMENT_PLANNING_CALENDAR_MAX_DAYS,
    buildAppointmentPlanningEmployeeFilterOptions,
    buildAutomaticAppointmentPlanningCalendarDateRange,
    createDefaultAppointmentPlanningFilterState,
    getAppointmentPlanningVisibleRows,
    inclusiveAppointmentPlanningDayCount,
    serializeAppointmentPlanningFilters,
    validateAppointmentPlanningCalendarDateRange,
    type CompleteAppointmentPlanningDateRange,
    type AppointmentPlanningTableSortState,
  } from "./_shared/_logic/filters"
  import { buildAppointmentPlanningPreviewColumns } from "./_logic/columns"
  import { serializeAppointmentPlanningPreviewRowsForRefresh } from "./_logic/rowRefresh"
  import type { AppointmentPlanningPreviewRow } from "../../../../../../../api/kehrblatt/planning"
  import type { WorkingHoursByUser } from "../../../../../../../_shared/_logic/form-state"
  import type {
    AppointmentPlanningCalendarSource,
    AppointmentPlanningCalendarAppointmentChange,
  } from "../../_shared/_logic/calendar-contract"
  import type { BatchTableSelectionChangeDetail } from "../../../../_logic/batch-apply-targets"
  import type {
    AppointmentPlanningDateRangeFilter,
    AppointmentPlanningFilterField,
    AppointmentPlanningFilterState,
  } from "../../../../_logic/appointments-contract"
  import {
    createStableRowNumberState,
    stableRowNumberColumn,
    stableRowNumberSort,
    withStableRowNumbers,
    withoutStableRowNumbers,
  } from "../../../../_logic/stable-row-numbers"

  type PreviewViewMode = "overview" | "calendar" | "table"
  type TableSelectionContext = {
    selectedRows: DataRow[]
    clearSelection: () => void
  }
  type Props = {
    title: string
    startBound: AppointmentPlanningStartBound
    count?: number | string | null
    description?: string | null
    showPreview?: boolean
    loading?: boolean
    tableError?: string | null
    rows: PlanningPreviewRow[]
    hiddenRowIds?: string[]
    deletingRowId?: string | null
    disabled?: boolean
    onRowChange: (row: PlanningPreviewRow, change: PlanningPreviewRowChange) => void
    onRemove?: (row: PlanningPreviewRow) => Promise<void> | void
    onRestore?: (row: PlanningPreviewRow) => Promise<void> | void
    onResetRow?: (row: PlanningPreviewRow) => Promise<void> | void
    onRemoveRows?: (rows: PlanningPreviewRow[]) => Promise<void> | void
    onResetRows?: (rows: PlanningPreviewRow[]) => Promise<void> | void
    onEditContactAvailability?: (row: PlanningPreviewRow) => Promise<void> | void
    onCalendarAppointmentChange?: (detail: AppointmentPlanningCalendarAppointmentChange) => void
    organizationId?: string | null
    calendarRows?: AppointmentPlanningPreviewRow[]
    calendarSources?: AppointmentPlanningCalendarSource[]
    employeeOptions?: PlanningPreviewSelectOption[]
    employeeLabelById?: Record<string, string>
    workingHoursByUser?: WorkingHoursByUser
    allowCalendarAppointmentResize?: boolean
    readOnlyRowIds?: string[]
    rowNumberById?: Record<string, number>
    showPreconfiguration?: boolean | null
    showMetrics?: boolean | null
    showFooter?: boolean | null
    helpModalTextId?: string
    preconfiguration?: Snippet | null
    metrics?: Snippet | null
    footer?: Snippet | null
    primaryAction?: Snippet | null
    enableSelection?: boolean
    onApplyScopeChange?: (detail: { selectedIds: string[] }) => void
  }

  const {
    title,
    startBound,
    count = null,
    description = null,
    showPreview = true,
    loading = false,
    tableError = null,
    rows,
    hiddenRowIds = [],
    deletingRowId = null,
    disabled = false,
    onRowChange,
    onRemove = undefined,
    onRestore = undefined,
    onResetRow = undefined,
    onRemoveRows = undefined,
    onResetRows = undefined,
    onEditContactAvailability = undefined,
    onCalendarAppointmentChange = undefined,
    organizationId = null,
    calendarRows = [],
    calendarSources = [],
    employeeOptions = [],
    employeeLabelById = {},
    workingHoursByUser = {},
    allowCalendarAppointmentResize = false,
    readOnlyRowIds = [],
    rowNumberById = {},
    showPreconfiguration = null,
    showMetrics = null,
    showFooter = null,
    helpModalTextId = "",
    preconfiguration: preconfigurationSnippet = null,
    metrics: metricsSnippet = null,
    footer: footerSnippet = null,
    primaryAction = null,
    enableSelection = false,
    onApplyScopeChange = undefined,
  }: Props = $props()

  let filterState = $state<AppointmentPlanningFilterState>(
    createDefaultAppointmentPlanningFilterState(),
  )
  let calendarDateRangeOverride = $state<CompleteAppointmentPlanningDateRange | null>(null)
  let calendarDateRangeError = $state<string | null>(null)
  let previewViewMode = $state<PreviewViewMode>("overview")
  let selectedIds = $state<string[]>([])
  let updateTableRows = $state<TableRowsUpdater | null>(null)
  let tableRowsSyncQueued = false
  const rowNumberState = createStableRowNumberState()

  const defaultSort: AppointmentPlanningTableSortState = stableRowNumberSort

  const columns = $derived<ColumnDef[]>([
    stableRowNumberColumn(),
    ...buildAppointmentPlanningPreviewColumns(),
  ])

  const loadingRows = $derived<LoadingPreviewRow[]>(
    Array.from({ length: rows.length ? Math.min(rows.length, 8) : 3 }, (_, index) => ({
      id: `preview-loading-${index}`,
      isLoading: true,
    })),
  )
  const hiddenRowIdSet = $derived(new Set(hiddenRowIds.map(String).filter(Boolean)))
  const numberedRows = $derived(
    withStableRowNumbers(rows, rowNumberState).map((row) => {
      const workflowRowNumber = rowNumberById[row.id]

      return Number.isFinite(workflowRowNumber) ? { ...row, rowNumber: workflowRowNumber } : row
    }),
  )
  const visibleNumberedRows = $derived(numberedRows.filter((row) => !hiddenRowIdSet.has(row.id)))
  const tableRows = $derived<PreviewTableRow[]>(loading ? loadingRows : visibleNumberedRows)
  const tablePageSize = $derived(Math.max(tableRows.length, 1))
  const filterableNumberedRows = $derived(visibleNumberedRows.filter((row) => !row.removed))
  const employeeFilterOptions = $derived(
    buildAppointmentPlanningEmployeeFilterOptions(filterableNumberedRows),
  )
  const effectiveEmployeeId = $derived(
    employeeFilterOptions.some((option) => option.value === filterState.employeeId)
      ? filterState.employeeId
      : "",
  )
  const tableFilters = $derived<AppointmentPlanningFilterState>({
    search: filterState.search,
    employeeId: effectiveEmployeeId,
    dateRange: filterState.dateRange,
  })
  const automaticCalendarDateRange = $derived(
    buildAutomaticAppointmentPlanningCalendarDateRange(calendarRows),
  )
  const automaticCalendarDateRangeTooLarge = $derived(
    Boolean(
      automaticCalendarDateRange &&
      inclusiveAppointmentPlanningDayCount(
        automaticCalendarDateRange.start,
        automaticCalendarDateRange.end,
      ) > APPOINTMENT_PLANNING_CALENDAR_MAX_DAYS,
    ),
  )
  const automaticCalendarDateRangeError = $derived(
    automaticCalendarDateRangeTooLarge
      ? "Der automatisch ermittelte Planungszeitraum überschreitet die maximalen 16 Wochen."
      : null,
  )
  const effectiveCalendarDateRange = $derived<AppointmentPlanningDateRangeFilter>(
    calendarDateRangeOverride ?? automaticCalendarDateRange ?? { start: null, end: null },
  )
  const calendarFilters = $derived<AppointmentPlanningFilterState>({
    search: filterState.search,
    employeeId: effectiveEmployeeId,
    dateRange: effectiveCalendarDateRange,
  })
  const calendarUnavailableReason = $derived(
    calendarDateRangeOverride ? null : automaticCalendarDateRangeError,
  )
  const previewViewModeOptions = [
    { label: "Übersicht", value: "overview", icon: mdiCalendarMonthOutline },
    { label: "Kalenderansicht", value: "calendar", icon: mdiCalendar },
    { label: "Tabellenansicht", value: "table", icon: mdiTable },
  ]

  const rowRefreshKey = $derived(serializeAppointmentPlanningPreviewRowsForRefresh(tableRows))
  const refreshKey = $derived(
    `${loading ? "loading" : "ready"}:${serializeAppointmentPlanningFilters(tableFilters)}:${rowRefreshKey}`,
  )
  const shouldShowPreconfiguration = $derived(
    Boolean(preconfigurationSnippet) && showPreconfiguration !== false,
  )
  const shouldShowFilters = $derived(Boolean(showPreview))
  const shouldShowMetrics = $derived(Boolean(metricsSnippet) && showMetrics !== false)
  const shouldShowFooter = $derived(Boolean(footerSnippet) && showFooter !== false)

  const getRows: TableGetRows = (params) => {
    const { limit, offset } = params
    const sort = (params as { sort?: AppointmentPlanningTableSortState }).sort
    const pageSize = limit && limit > 0 ? limit : 25
    const visibleRows = loading
      ? tableRows
      : getAppointmentPlanningVisibleRows(visibleNumberedRows, tableFilters, sort ?? defaultSort)
    const selectableIdSet = new Set(
      visibleRows
        .filter(
          (row): row is PlanningPreviewRow =>
            !("isLoading" in row) && !(row as PlanningPreviewRow).removed && !row.committed,
        )
        .map((row) => row.id),
    )
    const nextSelectedIds = selectedIds.filter((id) => selectableIdSet.has(id))

    if (
      nextSelectedIds.length !== selectedIds.length ||
      nextSelectedIds.some((id, index) => id !== selectedIds[index])
    ) {
      selectedIds = nextSelectedIds
    }

    onApplyScopeChange?.({ selectedIds })

    return {
      rows: visibleRows.slice(offset, offset + pageSize),
      total: visibleRows.length,
    }
  }

  const syncLoadedTableRows = () => {
    if (!updateTableRows) return

    const rowById = new Map(
      tableRows
        .filter((row): row is PlanningPreviewRow => !("isLoading" in row))
        .map((row) => [row.id, row]),
    )
    updateTableRows((currentRows) =>
      currentRows.map((row) => {
        if ("isLoading" in row) return row

        const replacement = rowById.get(String((row as PlanningPreviewRow).id))

        return replacement ?? row
      }),
    )
  }

  const requestTableRowsSync = () => {
    if (tableRowsSyncQueued) return

    tableRowsSyncQueued = true
    queueMicrotask(() => {
      void tick().then(() => {
        tableRowsSyncQueued = false
        syncLoadedTableRows()
      })
    })
  }

  const handleRowChange = (row: PlanningPreviewRow, change: PlanningPreviewRowChange) => {
    if (row.removed) return

    onRowChange(row, change)
    requestTableRowsSync()
  }

  const handleSelectionChange = (detail: BatchTableSelectionChangeDetail) => {
    selectedIds = detail.selectedIds
    onApplyScopeChange?.({ selectedIds })
  }

  const clearSelection = () => {
    if (!selectedIds.length) return

    selectedIds = []
    onApplyScopeChange?.({ selectedIds })
  }

  const handleTableFilterChange = (nextFilters: AppointmentPlanningFilterState) => {
    filterState = nextFilters
  }

  const handleCalendarFilterChange = (
    nextFilters: AppointmentPlanningFilterState,
    field: AppointmentPlanningFilterField,
  ) => {
    filterState = {
      ...filterState,
      search: nextFilters.search,
      employeeId: nextFilters.employeeId,
    }

    if (field !== "dateRange") return

    const result = validateAppointmentPlanningCalendarDateRange(nextFilters.dateRange)

    if (result.kind === "reset") {
      calendarDateRangeOverride = null
      calendarDateRangeError = null

      return
    }

    if (result.kind === "invalid") {
      calendarDateRangeError = result.message

      return
    }

    calendarDateRangeOverride = result.range
    calendarDateRangeError = null
  }

  const changePreviewViewMode = (value: unknown) => {
    const nextMode = value === "table" ? "table" : value === "calendar" ? "calendar" : "overview"

    if (nextMode !== "table") clearSelection()

    previewViewMode = nextMode
  }

  const rowControlsFor: PlanningTableRowControlsResolver = (
    row: DataRow,
  ): PlanningTableRowControlCommand[] => {
    if ("isLoading" in row) return []

    const preview = row as PlanningPreviewRow

    if (preview.committed) return []

    if (preview.removed) {
      return onRestore
        ? [
            {
              kind: "restore",
              disabled: disabled || loading,
              onClick: () => onRestore(preview),
            },
          ]
        : []
    }

    const controls: PlanningTableRowControlCommand[] = []

    if (onResetRow) {
      controls.push({
        kind: "reset",
        disabled: disabled || loading,
        onClick: () => onResetRow(preview),
      })
    }

    if (onRemove && preview.removable) {
      controls.push({
        kind: "remove",
        disabled: disabled || loading || deletingRowId === preview.id,
        onClick: () => onRemove(preview),
      })
    }

    return controls
  }

  const rowStateFor: PlanningTableRowStateResolver = (row: DataRow) => {
    if ("isLoading" in row) return { loading: true, disabled: true, selectable: false }

    const preview = row as PlanningPreviewRow

    return {
      disabled: preview.committed,
      selectable: !preview.removed && !preview.committed,
      muted: Boolean(preview.removed || preview.committed),
      disabledReason: preview.committed
        ? "Der Termin dieser Zeile wurde bereits gespeichert."
        : null,
      mutedReason: preview.removed
        ? "Diese Zeile wird in diesem Schritt ausgelassen."
        : preview.committed
          ? "Der Termin dieser Zeile wurde bereits gespeichert."
          : null,
    }
  }

  const resetSelectedRows = (selectedRows: DataRow[]) => {
    if (!onResetRows) return

    void onResetRows(withoutStableRowNumbers(selectedRows as PlanningPreviewRow[]))
  }

  const removeSelectedRows = (selectedRows: DataRow[]) => {
    if (!onRemoveRows) return

    void onRemoveRows(withoutStableRowNumbers(selectedRows as PlanningPreviewRow[]))
  }
</script>

<TableStepLayout
  {title}
  {count}
  {description}
  darkMode={true}
  showPreconfiguration={shouldShowPreconfiguration}
  showFilters={shouldShowFilters}
  showMetrics={shouldShowMetrics}
  showFooter={shouldShowFooter}
  {helpModalTextId}
>
  {#snippet preconfiguration()}
    {#if preconfigurationSnippet}
      {@render preconfigurationSnippet()}
    {/if}
  {/snippet}

  {#snippet titleActions()}
    <div class="appointment-preview-title-actions">
      <ToggleCTA
        class="appointment-preview-view-toggle"
        dataTestid="appointment-preview-view-toggle"
        options={previewViewModeOptions}
        value={previewViewMode}
        darkMode={true}
        onChange={changePreviewViewMode}
      />
      {#if shouldShowFooter && primaryAction}
        {@render primaryAction()}
      {/if}
    </div>
  {/snippet}

  {#snippet filters()}
    {#if showPreview && previewViewMode !== "table"}
      <AppointmentPlanningFilterBar
        filters={calendarFilters}
        employeeOptions={employeeFilterOptions}
        disabled={disabled || loading}
        error={calendarDateRangeError ?? calendarUnavailableReason}
        onChange={handleCalendarFilterChange}
      />
    {:else if showPreview}
      <AppointmentPlanningFilterBar
        filters={tableFilters}
        employeeOptions={employeeFilterOptions}
        disabled={disabled || loading}
        onChange={handleTableFilterChange}
      />
    {/if}
  {/snippet}

  {#snippet metrics()}
    {#if metricsSnippet}
      {@render metricsSnippet()}
    {/if}
  {/snippet}

  {#snippet footer()}
    {#if footerSnippet}
      {@render footerSnippet()}
    {/if}
  {/snippet}

  {#if showPreview}
    <div class="appointment-preview-content">
      {#if previewViewMode !== "table" && organizationId}
        <AppointmentPlanningCalendarPreview
          overview={previewViewMode === "overview"}
          {startBound}
          {organizationId}
          rows={calendarRows}
          planningRows={filterableNumberedRows}
          sources={calendarSources}
          search={calendarFilters.search}
          employeeId={calendarFilters.employeeId}
          dateRange={calendarFilters.dateRange}
          unavailableReason={calendarUnavailableReason}
          {employeeOptions}
          {employeeLabelById}
          {workingHoursByUser}
          allowAppointmentResize={allowCalendarAppointmentResize}
          {readOnlyRowIds}
          loadingPreview={loading}
          onAppointmentChange={onCalendarAppointmentChange}
        />
      {:else}
        {#snippet batchActions(context: TableSelectionContext)}
          <InboxWorkflowDefaultBulkActions
            selectedRows={context.selectedRows}
            resetRows={resetSelectedRows}
            removeRows={removeSelectedRows}
            clearSelection={context.clearSelection}
            readOnly={disabled}
            disabled={disabled || loading}
          />
        {/snippet}
        <AppointmentPlanningPreviewTable
          {startBound}
          {columns}
          {getRows}
          {refreshKey}
          {defaultSort}
          pageSize={tablePageSize}
          showActions={Boolean(onRemove || onEditContactAvailability)}
          rowControls={onRemove || onRestore || onResetRow ? rowControlsFor : undefined}
          getRowState={rowStateFor}
          {enableSelection}
          {selectedIds}
          batchActions={onResetRows && onRemoveRows ? batchActions : undefined}
          onSelectionChange={handleSelectionChange}
          onRowsUpdaterReady={(updater) => {
            updateTableRows = updater
          }}
          {disabled}
          onRowChange={handleRowChange}
          {onEditContactAvailability}
        />
      {/if}
      {#if tableError}
        <div class="appointment-preview-error" role="alert">
          <strong>Terminplanung konnte nicht geladen werden.</strong>
          <span>{tableError}</span>
        </div>
      {/if}
    </div>
  {/if}
</TableStepLayout>

<style lang="less">
  .appointment-preview-title-actions {
    display: flex;
    align-items: stretch;
    gap: 0.45rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    min-width: 0;
    max-width: 100%;
  }

  .appointment-preview-title-actions :global(.appointment-preview-view-toggle) {
    display: inline-flex;
    flex: 0 0 auto;
    padding: 0;
    max-width: 100%;
  }

  .appointment-preview-title-actions :global(.appointment-preview-view-toggle button.cta) {
    padding: 0.5rem 0.75rem;
  }

  .appointment-preview-title-actions :global(.appointment-preview-view-toggle button.cta.active) {
    box-shadow: inset 0 0 0 1px currentColor;
  }

  .appointment-preview-content {
    position: relative;
    min-height: 12rem;
  }

  .appointment-preview-error {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 1.5rem;
    color: #fff;
    text-align: center;
    background: rgb(13 15 17 / 82%);
  }

  .appointment-preview-error strong {
    color: #ff7373;
  }
</style>
